import type { ButtonHTMLAttributes } from 'react';

export type UiIconName =
  | 'translate' | 'copy' | 'edit' | 'more' | 'scale' | 'export'
  | 'media' | 'history' | 'trash' | 'production' | 'close' | 'add' | 'search' | 'back' | 'check'
  | 'chevronRight' | 'chevronDown' | 'home' | 'library' | 'menu' | 'open' | 'refresh';

export function UiIcon({ name, size = 18 }: { name: UiIconName; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (name === 'translate') return <svg {...common}><path d="M4 5h9M8.5 3v2M6 9c1.2 2.6 3.4 4.6 6 5.8M11 5c-.7 4.2-3 7.3-6.7 9"/><path d="M14 20l3.5-9 3.5 9M15.4 16.5h4.2"/></svg>;
  if (name === 'copy') return <svg {...common}><rect x="8" y="8" width="10" height="11" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h2"/></svg>;
  if (name === 'edit') return <svg {...common}><path d="M4 20h4l11-11a2.2 2.2 0 0 0-4-4L4 16v4Z"/><path d="m13.5 6.5 4 4"/></svg>;
  if (name === 'more') return <svg {...common}><circle cx="5" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.2" fill="currentColor" stroke="none"/></svg>;
  if (name === 'scale') return <svg {...common}><path d="M4 7h11M12 4l3 3-3 3M20 17H9M12 14l-3 3 3 3"/></svg>;
  if (name === 'export') return <svg {...common}><path d="M12 3v12M8 7l4-4 4 4"/><path d="M5 13v6h14v-6"/></svg>;
  if (name === 'media') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="m5 17 4.5-4 3 2.5 2.5-2 4 3.5"/></svg>;
  if (name === 'history') return <svg {...common}><path d="M4 6v5h5"/><path d="M5.3 11A7 7 0 1 0 7 6.5"/><path d="M12 8v4l3 2"/></svg>;
  if (name === 'trash') return <svg {...common}><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13"/><path d="M10 11v5M14 11v5"/></svg>;
  if (name === 'production') return <svg {...common}><path d="M4 19V9l8-4 8 4v10"/><path d="M8 19v-5h8v5M8 10h.01M12 10h.01M16 10h.01"/></svg>;
  if (name === 'close') return <svg {...common}><path d="m6 6 12 12M18 6 6 18"/></svg>;
  if (name === 'add') return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
  if (name === 'search') return <svg {...common}><circle cx="11" cy="11" r="6"/><path d="m16 16 4 4"/></svg>;
  if (name === 'back') return <svg {...common}><path d="m15 5-7 7 7 7"/><path d="M8 12h12"/></svg>;
  if (name === 'check') return <svg {...common}><path d="m5 12 4 4 10-10"/></svg>;
  if (name === 'chevronRight') return <svg {...common}><path d="m9 6 6 6-6 6"/></svg>;
  if (name === 'chevronDown') return <svg {...common}><path d="m6 9 6 6 6-6"/></svg>;
  if (name === 'home') return <svg {...common}><path d="m4 10 8-6 8 6v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1v-9Z"/></svg>;
  if (name === 'library') return <svg {...common}><path d="M5 4h5v16H5zM10 4h5v16h-5zM15 5l4-1 2 15-4 1z"/></svg>;
  if (name === 'menu') return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>;
  if (name === 'open') return <svg {...common}><path d="M14 5h5v5M19 5l-8 8"/><path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>;
  if (name === 'refresh') return <svg {...common}><path d="M20 7v5h-5"/><path d="M4 17v-5h5"/><path d="M6.1 8.1A7 7 0 0 1 18.6 7L20 9"/><path d="M17.9 15.9A7 7 0 0 1 5.4 17L4 15"/></svg>;
  return null;
}

export function IconActionButton({ icon, label, hint, className = '', ...props }: {
  icon: UiIconName;
  label: string;
  hint?: string;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label' | 'title'>) {
  return <button
    {...props}
    type="button"
    className={`ux-icon-action ${className}`.trim()}
    aria-label={label}
    title={hint || label}
    data-tooltip={hint || label}
  >
    <UiIcon name={icon} />
    <span className="ux-icon-action-label">{label}</span>
  </button>;
}
